'use client';

import { useLocale } from 'next-intl';
import { useCallback, useEffect, useState } from 'react';

import { useBookingsQuery } from '@website/app/[locale]/bookings/_hooks/useBookingsQuery';
import { isFeatureEnabled } from '@website/config/featureFlags';
import { useCurrentUser } from '@website/hooks/useCurrentUser';
import { useInstallContext } from '@website/hooks/useInstallContext';

const VISITED_STORAGE_KEY = 'toptoptime.installPromptVisitedAt';

/**
 * Read the "user has visited the install page" timestamp from localStorage.
 * SSR-safe: returns `null` on the server. We deliberately check `typeof
 * window` rather than `typeof localStorage` because Safari Private Browsing
 * historically threw on `localStorage` access — the explicit window check
 * also short-circuits during SSR before any DOM is available.
 */
function readVisitedAt(): number | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(VISITED_STORAGE_KEY);
    if (!raw) return null;
    const parsed = Number.parseInt(raw, 10);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    // Quota exceeded / private mode / disabled storage — treat as unvisited
    // rather than crashing the nudge logic. Worst case: user sees the badge
    // every session, which is harmless.
    return null;
  }
}

function writeVisitedAt(timestamp: number) {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(VISITED_STORAGE_KEY, String(timestamp));
  } catch {
    // Same fallback as above — silent. The badge will simply re-appear on
    // the next page load, which is the only observable consequence.
  }
}

export interface InstallNudgeState {
  /**
   * True when the menu entry should be shown at all. Drives whether the
   * "Install Application" row appears in the Settings menu.
   */
  showMenuEntry: boolean;
  /**
   * True when the menu entry should attract attention with a +1 badge on
   * the Settings tab and a small indicator on the row itself.
   */
  showNudgeBadge: boolean;
  /**
   * Mark the install page as visited. After this, `showNudgeBadge` becomes
   * false but `showMenuEntry` stays true so the user can still install later.
   */
  markVisited: () => void;
}

/**
 * Owns the "should we suggest installing the PWA" decision.
 *
 * Visibility rules (per design doc):
 * - Menu entry: only on mobile (iOS / Android), only when not already in
 *   standalone mode. Always shown there — users can install whenever.
 * - Nudge badge: layered on top, shown only when the user has demonstrated
 *   commitment (any professional, OR a learner with at least one confirmed
 *   booking — `upcoming` or `past` — which excludes pending/expired/cancelled
 *   payment states). Cleared once they've opened the install page.
 *
 * Storage is intentionally per-device (localStorage) rather than per-account
 * because the menu entry is mobile-only and the cross-device "I have two
 * phones" case is rare enough not to warrant backend plumbing. The trade-off
 * is documented in the design plan.
 */
export function useInstallNudge(): InstallNudgeState {
  const { isMobile, isStandalone, hasMounted } = useInstallContext();
  const { data: user } = useCurrentUser();
  const locale = useLocale();
  // We reuse the same `useBookingsQuery` cache key shape as `BookingsPageClient`
  // and `useUpcomingBookingsCount` so this hook never triggers an extra
  // network call when those are already mounted. When this hook is the only
  // consumer (rare — settings/bottom-nav don't mount bookings), we still get
  // a single fetch and the same 5-min staleTime keeps it cheap.
  const { data: bookings } = useBookingsQuery(user ?? null, { locale });

  // Visit timestamp lives in localStorage and is treated as a single source
  // of truth. We mirror it into React state so callers re-render when
  // `markVisited` is called — without this, the badge would keep showing
  // until the next route change.
  const [visitedAt, setVisitedAt] = useState<number | null>(null);

  useEffect(() => {
    if (!hasMounted) return;
    setVisitedAt(readVisitedAt());
  }, [hasMounted]);

  const markVisited = useCallback(() => {
    const now = Date.now();
    writeVisitedAt(now);
    setVisitedAt(now);
  }, []);

  // SSR / pre-mount guard — avoid flashing the entry/badge during hydration.
  if (!hasMounted) {
    return { showMenuEntry: false, showNudgeBadge: false, markVisited };
  }

  // Feature flag: PWA promotion is rolled out per stage. When disabled
  // we hide both the Settings menu entry and the nudge badge — the
  // /settings/install page itself stays routable so existing home-screen
  // icons (which may have been added before a rollback) still work.
  if (!isFeatureEnabled('PWA_INSTALL')) {
    return { showMenuEntry: false, showNudgeBadge: false, markVisited };
  }

  const showMenuEntry = isMobile && !isStandalone;
  if (!showMenuEntry) {
    return { showMenuEntry: false, showNudgeBadge: false, markVisited };
  }

  // Eligibility:
  // - Professionals: nudged immediately on first sign-in. They opted into the
  //   platform; suggesting they install the home-screen icon is appropriate
  //   from day one.
  // - Learners: nudged once they have at least one *committed* booking. The
  //   `upcoming` and `past` buckets in `getUnifiedBookings` already filter to
  //   confirmed/cash/free states (excluding PENDING_PAYMENT, EXPIRED, FAILED,
  //   PROCESSING_PAYMENT, REFUNDED). We don't want to nudge people who only
  //   abandoned a checkout, hence the bucket-based check rather than scanning
  //   raw paymentStatus strings here.
  const isProfessional = user?.isProfessional === true;
  const hasCommittedBooking = (bookings?.upcoming.length ?? 0) + (bookings?.past.length ?? 0) > 0;
  const isEligibleForNudge = !!user && (isProfessional || hasCommittedBooking);

  const showNudgeBadge = isEligibleForNudge && visitedAt === null;

  return { showMenuEntry: true, showNudgeBadge, markVisited };
}
